import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { driverRecordShape, routeStopShape } from './driverRecordShape';
import styledComponent from '../../../utils/styledComponent';
import theme from '../../../theme';
import StopPopupContent from './StopPopupContent';
import PollCountdown from './PollCountdown';
import { DEFAULT_POLL_INTERVAL_MS } from '../../../hooks/useDriverLocationsPoll';
import Button from '../../../components/atoms/forms/Button';
import { STOP_STATE_LABEL, ROW_TONE_BY_STATUS } from './statusStyles';
import {
  formatClockTime,
  formatDurationMinutes,
  formatDriverNames,
} from '../../../utils/displayFormat';
import {
  ON_TRACK_LIMIT_MIN,
  BEHIND_LIMIT_MIN,
  byStopIndex,
} from '../../../utils/composeDriverRecords';

function formatClock(ms) {
  return Number.isFinite(ms)
    ? formatClockTime(new Date(ms), { zone: true })
    : null;
}

// etaDeltaMin → "2h 11m late" / "On time" / "—".
function formatDeviation(min) {
  if (min === null || min === undefined) return '—';
  if (min === 0) return 'On time';
  return `${formatDurationMinutes(min)} ${min >= 0 ? 'late' : 'early'}`;
}

function deviationToneClass(min) {
  if (min === null || min === undefined) return 'rlv-dev-neutral';
  if (min > BEHIND_LIMIT_MIN) return 'rlv-dev-late';
  if (min > ON_TRACK_LIMIT_MIN) return 'rlv-dev-warn';
  if (min < -ON_TRACK_LIMIT_MIN) return 'rlv-dev-early';
  return 'rlv-dev-ontime';
}

const StyledDiv = styledComponent('div', {
  background: theme.colors.bgCard,
  borderRadius: theme.radii.card,
  border: theme.borders.card,
  boxShadow: theme.shadows.card,
  padding: theme.spacing.cardPadding,

  '& .rlv-caveat': {
    fontSize: theme.sizes.smallFontSize,
    color: theme.colors.textMuted,
    fontStyle: 'italic',
    marginBottom: '12px',
  },
  '& .rlv-scroll': {
    overflowX: 'auto',
    overflowY: 'auto',
    maxHeight: 'calc(100vh - 320px)',
  },
  '& .rlv-table': {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: theme.sizes.bodyFontSize,
    color: theme.colors.textBody,
  },
  '& .rlv-table th': {
    textAlign: 'left',
    fontSize: theme.sizes.captionFontSize,
    fontWeight: theme.fontWeights.semibold,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    color: theme.colors.textMuted,
    padding: '8px 16px 10px',
    whiteSpace: 'nowrap',
    borderBottom: `1px solid ${theme.colors.borderLight}`,
    // Keep the header visible while the body scrolls vertically.
    position: 'sticky',
    top: 0,
    background: theme.colors.bgCard,
    zIndex: 1,
  },
  '& .rlv-table td': {
    padding: '14px 16px',
    borderBottom: `1px solid ${theme.colors.borderLight}`,
    verticalAlign: 'middle',
  },
  '& .rlv-table tr:last-child td': {
    borderBottom: 'none',
  },
  '& .rlv-row.on-time td': { background: theme.colors.statusOnTrackBg },
  '& .rlv-row.late td': { background: theme.colors.statusOffTrackBg },
  '& .rlv-route': {
    fontWeight: theme.fontWeights.semibold,
    whiteSpace: 'nowrap',
  },
  '& .rlv-muted': {
    color: theme.colors.textMuted,
  },
  '& .rlv-tabnum': {
    fontVariantNumeric: 'tabular-nums',
    whiteSpace: 'nowrap',
  },

  '& .rlv-progress': {
    display: 'flex',
    alignItems: 'center',
    gap: '3px',
    paddingTop: '8px',
    minWidth: '160px',
  },
  '& .rlv-seg': {
    appearance: 'none',
    padding: 0,
    margin: 0,
    width: '12px',
    height: '15px',
    borderRadius: '2px',
    border: 'none',
    flex: '0 0 auto',
    cursor: 'pointer',
  },
  // Filled grey rather than hollow: a transparent square all but vanished on
  // the tinted (late / on-time) rows.
  '& .rlv-seg.is-pending': {
    background: theme.colors.statusPending,
  },
  '& .rlv-seg.is-done': {
    background: theme.colors.statusOnTrack,
  },
  '& .rlv-seg.is-done.is-out-of-seq': {
    background: theme.colors.statusOutOfSequence,
  },
  '& .rlv-seg.is-current': {
    position: 'relative',
    background: theme.colors.statusBehind,
    boxShadow: `0 0 0 1px ${theme.colors.white}`,
  },
  '& .rlv-seg.is-current::before': {
    content: '""',
    position: 'absolute',
    top: '-7px',
    left: '50%',
    transform: 'translateX(-50%)',
    borderLeft: '4px solid transparent',
    borderRight: '4px solid transparent',
    borderTop: `5px solid ${theme.colors.textDark}`,
  },
  '& .rlv-seg:focus-visible': {
    outline: 'none',
    boxShadow: `0 0 0 2px ${theme.colors.infoBlue}66`,
  },

  '& .rlv-dev-late': {
    color: theme.colors.statusOffTrack,
    fontWeight: theme.fontWeights.semibold,
  },
  '& .rlv-dev-warn': {
    color: theme.colors.statusBehind,
    fontWeight: theme.fontWeights.semibold,
  },
  '& .rlv-dev-early': { color: theme.colors.statusOnTrack },
  '& .rlv-dev-ontime': { color: theme.colors.statusOnTrack },
  '& .rlv-dev-neutral': { color: theme.colors.textMuted },

  '& .rlv-poll': {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: theme.spacing.gapXl,
  },
  '& .rlv-empty': {
    padding: '40px 16px',
    textAlign: 'center',
    color: theme.colors.textMuted,
    fontSize: theme.sizes.bodyFontSize,
  },
});

// The popover bubble: provides the white card + positioning + close button; the
// stop detail itself is the shared StopPopupContent (same as the map marker).
const StyledPopover = styledComponent('div', {
  position: 'fixed',
  zIndex: 1300,
  width: '300px',
  background: theme.colors.bgCard,
  borderRadius: theme.radii.card,
  boxShadow: theme.shadows.dropdown,
  border: `1px solid ${theme.colors.borderLight}`,

  '& .rlv-pop-close': {
    position: 'absolute',
    top: '8px',
    right: '8px',
    width: '24px',
    height: '24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: 'none',
    background: 'none',
    borderRadius: theme.radii.input,
    color: theme.colors.textMuted,
    cursor: 'pointer',
    fontSize: theme.sizes.headingFontSize,
    lineHeight: 1,
  },
  '& .rlv-pop-close:hover': {
    background: theme.colors.bgMuted,
    color: theme.colors.textDark,
  },
});

function StopPopover({ active, pinned, onClose }) {
  const { stop, driver, rect } = active;
  const below = rect.top < 280;
  const vw = window.innerWidth;
  const centerX = Math.min(Math.max(rect.left + rect.width / 2, 160), vw - 160);
  const style = {
    pointerEvents: pinned ? 'auto' : 'none',
    left: centerX,
    ...(below
      ? { top: rect.bottom + 10, transform: 'translateX(-50%)' }
      : { top: rect.top - 10, transform: 'translate(-50%, -100%)' }),
  };

  return createPortal(
    <StyledPopover className="rlv-pop" style={style} role="tooltip">
      {pinned && (
        <button
          type="button"
          className="rlv-pop-close"
          aria-label="Close stop detail"
          onClick={onClose}
        >
          ✕
        </button>
      )}
      <StopPopupContent stop={stop} driver={driver} />
    </StyledPopover>,
    document.body,
  );
}

StopPopover.propTypes = {
  active: PropTypes.shape({
    stop: routeStopShape,
    driver: PropTypes.shape({
      driverName: PropTypes.string,
      routeName: PropTypes.string,
      stopsTotal: PropTypes.number,
    }),
    rect: PropTypes.object,
  }).isRequired,
  pinned: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

function RouteProgress({ stops, driver, title, onShow, onHide, onToggle }) {
  return (
    <div className="rlv-progress" title={title}>
      {stops.map((s) => {
        // Anything that isn't done/current renders as pending, matching the
        // aria-label fallback below.
        const state =
          s.state === 'done' || s.state === 'current' ? s.state : 'pending';
        const cls = `rlv-seg is-${state}${
          state === 'done' && s.outOfSequence ? ' is-out-of-seq' : ''
        }`;
        const payload = (el) => ({
          stop: s,
          driver,
          rect: el.getBoundingClientRect(),
        });
        return (
          <button
            key={`${s.stopIndex}-${s.stopId}`}
            type="button"
            className={cls}
            aria-label={`Stop ${s.stopIndex} · ${
              STOP_STATE_LABEL[s.state] || 'Pending'
            }`}
            onMouseEnter={(e) => onShow(payload(e.currentTarget))}
            onMouseLeave={onHide}
            onFocus={(e) => onShow(payload(e.currentTarget))}
            onBlur={onHide}
            onClick={(e) => onToggle(payload(e.currentTarget))}
          />
        );
      })}
    </div>
  );
}

RouteProgress.propTypes = {
  stops: PropTypes.arrayOf(routeStopShape).isRequired,
  driver: PropTypes.shape({
    driverNames: PropTypes.arrayOf(PropTypes.string),
    routeName: PropTypes.string,
    stopsTotal: PropTypes.number,
  }).isRequired,
  title: PropTypes.string.isRequired,
  onShow: PropTypes.func.isRequired,
  onHide: PropTypes.func.isRequired,
  onToggle: PropTypes.func.isRequired,
};

// Native-tooltip text for the progress bar: the planned position of the stop
// the driver is working now (or the first pending one when nothing is in
// flight yet).
function progressTitle(driver, stops) {
  const total = driver.stopsTotal || stops.length;
  if (driver.status === 'completed') {
    return `All ${total} stops stocked`;
  }
  if (driver.status === 'not-started') {
    return `Not started · ${total} stops`;
  }
  const active =
    stops.find((stop) => stop.state === 'current') ||
    stops.find((stop) => stop.state === 'pending');
  if (!active) {
    return `${driver.stopsCompleted} of ${total} stops stocked`;
  }
  return `Stop ${active.stopIndex} of ${total} planned`;
}

function buildRow(driver) {
  const stops = [...(driver.routeStops || [])].sort(byStopIndex);
  const startMs = stops
    .map((s) => s.plannedStartMs)
    .filter(Number.isFinite)
    .sort((a, b) => a - b)[0];
  const endMs = stops
    .map((s) => s.currentEtaMs ?? s.stockedAtMs ?? s.plannedEndMs)
    .filter(Number.isFinite)
    .sort((a, b) => b - a)[0];

  return {
    routeId: driver.routeId,
    routeName: driver.routeName,
    driverName: formatDriverNames(driver.driverNames, ''),
    scheduledStart: formatClock(startMs),
    endOfRoute: driver.backAtDepotLabel || formatClock(endMs),
    deviation: formatDeviation(driver.etaDeltaMin),
    deviationClass: deviationToneClass(driver.etaDeltaMin),
    stopsCompleted: driver.stopsCompleted,
    stopsTotal: driver.stopsTotal,
    stops,
    toneClass: ROW_TONE_BY_STATUS[driver.status] || '',
    progressTitle: progressTitle(driver, stops),
    // Minimal driver shape for the shared StopPopupContent footer.
    driver: {
      driverNames: driver.driverNames || [],
      routeName: driver.routeName,
      stopsTotal: driver.stopsTotal,
    },
  };
}

function RouteListView({
  drivers,
  isLoading,
  isApplying,
  lastPolledAt,
  pollIntervalMs,
  error,
  onSelectRoute,
}) {
  const rows = useMemo(
    () =>
      [...drivers]
        .sort((a, b) => (a.routeName || '').localeCompare(b.routeName || ''))
        .map(buildRow),
    [drivers],
  );

  // active = the stop whose popover is open; pinned = opened by click (stays
  // until ✕ / Escape / click-out), otherwise it's a transient hover preview.
  const [active, setActive] = useState(null);
  const [pinned, setPinned] = useState(false);
  const pinnedRef = useRef(false);
  const setPin = (v) => {
    pinnedRef.current = v;
    setPinned(v);
  };

  const show = (payload) => {
    if (!pinnedRef.current) setActive(payload);
  };
  const hide = () => {
    if (!pinnedRef.current) setActive(null);
  };
  const toggle = (payload) => {
    setActive(payload);
    setPin(true);
  };
  const close = () => {
    setPin(false);
    setActive(null);
  };

  useEffect(() => {
    if (!active) return undefined;
    const doClose = () => {
      pinnedRef.current = false;
      setPinned(false);
      setActive(null);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') doClose();
    };
    const onDown = (e) => {
      if (
        pinnedRef.current &&
        !e.target.closest('.rlv-seg') &&
        !e.target.closest('.rlv-pop')
      ) {
        doClose();
      }
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
  }, [active]);

  if (error) {
    return (
      <StyledDiv>
        <div className="rlv-empty">Couldn’t load routes. Try again.</div>
      </StyledDiv>
    );
  }

  // An empty list mid-load is a load, not an empty result. Guarded on
  // `isApplying` as well as `isLoading`: the hook drops isLoading between the
  // route-skeleton paint and the position fan-out, and a raw guard would
  // flash "No routes for the selected filters." in that gap.
  if (rows.length === 0) {
    return (
      <StyledDiv>
        <div className="rlv-empty">
          {isLoading || isApplying
            ? 'Loading routes…'
            : 'No routes for the selected filters.'}
        </div>
      </StyledDiv>
    );
  }

  return (
    <StyledDiv>
      <div className="rlv-caveat">
        Deviation and End of Route are estimates. Where the driver app has
        reported drive legs, ETAs use live drive times; otherwise they carry
        forward the current delay. Hover or click a stop for detail.
      </div>
      <div className="rlv-scroll">
        <table className="rlv-table">
          <thead>
            <tr>
              <th>Route</th>
              <th>Assigned To</th>
              <th>Scheduled Start</th>
              <th>Route Progress</th>
              <th>Deviation</th>
              <th>End of Route Est.</th>
              <th>Stops Completed</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr
                key={row.routeId}
                className={`rlv-row ${row.toneClass}`.trim()}
              >
                <td className="rlv-route">
                  <Button
                    variant="link-primary"
                    title="Open on map"
                    onClick={() => onSelectRoute(row.routeId)}
                  >
                    {row.routeName}
                  </Button>
                </td>
                <td className={row.driverName ? '' : 'rlv-muted'}>
                  {row.driverName || 'No driver'}
                </td>
                <td className="rlv-tabnum">{row.scheduledStart || '—'}</td>
                <td>
                  <RouteProgress
                    stops={row.stops}
                    driver={row.driver}
                    title={row.progressTitle}
                    onShow={show}
                    onHide={hide}
                    onToggle={toggle}
                  />
                </td>
                <td className={`rlv-tabnum ${row.deviationClass}`}>
                  {row.deviation}
                </td>
                <td className="rlv-tabnum">{row.endOfRoute || '—'}</td>
                <td className="rlv-tabnum">
                  {row.stopsCompleted}/{row.stopsTotal}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="rlv-poll">
        <PollCountdown
          lastPolledAt={lastPolledAt}
          intervalMs={pollIntervalMs}
          isLoading={isLoading}
        />
      </div>
      {active && (
        <StopPopover active={active} pinned={pinned} onClose={close} />
      )}
    </StyledDiv>
  );
}

RouteListView.propTypes = {
  drivers: PropTypes.arrayOf(driverRecordShape).isRequired,
  isLoading: PropTypes.bool,
  // True only while an Apply / first load is in flight (see MonitoringTab).
  isApplying: PropTypes.bool,
  lastPolledAt: PropTypes.instanceOf(Date),
  pollIntervalMs: PropTypes.number,
  error: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  onSelectRoute: PropTypes.func.isRequired,
};

RouteListView.defaultProps = {
  isLoading: false,
  isApplying: false,
  lastPolledAt: null,
  pollIntervalMs: DEFAULT_POLL_INTERVAL_MS,
  error: null,
};

export default RouteListView;
